import { useState, useEffect, useRef } from 'react';
import { useAuthContext } from '../../contexts/AuthContext';
import type { SessionViewProps } from './types';
import { formatTimer } from './formatTimer';
import { PracticeSessionReviewHeader } from './PracticeSessionReviewHeader';
import { SessionEvaluationCard } from './SessionEvaluationCard';
import { RecordingPrivateNotice } from './RecordingPrivateNotice';
import { SessionTranscriptPanel } from './SessionTranscriptPanel';
import { TranscriptToggleButton } from './TranscriptToggleButton';
import { practiceModeLabel } from '../../lib/practiceModeLabel';
export function TextSessionView({
  sessionInfo,
  messages,
  evaluation,
  loading,
  isCompleted,
  isOwnSession,
  remainingSeconds,
  ending,
  onSendMessage,
  onEndSession,
  onBack,
  sessionUsage,
  participants = [],
  speakingUsers: _speakingUsers,
  showTranscriptByDefault,
}: SessionViewProps) {
  const { user } = useAuthContext();
  const showCost = false;
  const recordingHidden = sessionInfo.access === 'summary';
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  /** Visible unless the profile explicitly disables the transcript column (verbal uses viewport-gated prefs). */
  const [showTranscript, setShowTranscript] = useState(() => showTranscriptByDefault !== false);

  useEffect(() => {
    setShowTranscript(showTranscriptByDefault !== false);
  }, [sessionInfo.id, showTranscriptByDefault]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Keep focus on the composer whenever the user can type: initial load, after the persona
  // responds (loading clears), and when opening another session.
  useEffect(() => {
    if (loading || isCompleted || !(isOwnSession || sessionInfo.isParticipant)) return;
    const raf = window.requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(raf);
  }, [sessionInfo.id, loading, isCompleted, isOwnSession, sessionInfo.isParticipant]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || loading) return;
    const msg = input.trim();
    setInput('');
    await onSendMessage(msg);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-7rem)] md:h-[calc(100vh-8rem)]">
      <PracticeSessionReviewHeader
        sessionInfo={sessionInfo}
        sessionUsage={sessionUsage}
        participants={participants}
        isOwnSession={isOwnSession}
        modeLabel={practiceModeLabel('text')}
        showCost={showCost}
      >
        <div data-tour="ps-text-header-actions" className="contents">
          {remainingSeconds != null && !isCompleted && (
            <span
              className={`tabular-nums text-xs sm:text-sm font-semibold px-2 sm:px-3 py-1 sm:py-1.5 rounded-md ${
                remainingSeconds <= 0
                  ? 'bg-red-200 text-red-800 animate-pulse'
                  : remainingSeconds <= 60
                    ? 'bg-red-100 text-red-700 animate-pulse'
                    : remainingSeconds <= 300
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-gray-100 text-gray-700'
              }`}
            >
              {formatTimer(remainingSeconds)}
            </span>
          )}
          {!isCompleted && isOwnSession && (
            <button
              type="button"
              onClick={onEndSession}
              disabled={ending}
              className="text-xs sm:text-sm bg-red-50 text-red-700 px-2 sm:px-3 py-1 sm:py-1.5 rounded-md hover:bg-red-100 transition-colors disabled:opacity-50"
            >
              {ending ? 'Evaluating...' : 'End Session'}
            </button>
          )}
          {!recordingHidden && (
            <TranscriptToggleButton
              showTranscript={showTranscript}
              onToggle={() => setShowTranscript((v) => !v)}
            />
          )}
          <button
            type="button"
            onClick={onBack}
            className="text-xs sm:text-sm text-gray-600 hover:text-gray-900"
          >
            <span className="hidden sm:inline">Back to List</span>
            <span className="sm:hidden">Back</span>
          </button>
        </div>
      </PracticeSessionReviewHeader>

      <div className="flex-1 flex flex-col md:flex-row gap-4 min-h-0">
        <div className="flex-1 min-w-0 flex flex-col gap-4 min-h-0 overflow-y-auto">
          {evaluation ? (
            <SessionEvaluationCard
              evaluation={evaluation}
              goalLabel={sessionInfo.goalLabel}
              feedbackHidden={recordingHidden}
            />
          ) : recordingHidden ? (
            <RecordingPrivateNotice />
          ) : null}
        </div>

        {showTranscript && !recordingHidden ? (
          <SessionTranscriptPanel
            headingId="text-session-transcript-heading"
            messages={messages}
            sessionInfo={sessionInfo}
            participants={participants}
            user={user}
            loading={loading}
            messagesEndRef={messagesEndRef}
            showMobileHideButton
            onHideTranscript={() => setShowTranscript(false)}
          />
        ) : null}
      </div>

      {!isCompleted && (isOwnSession || sessionInfo.isParticipant) && (
        <form
          data-tour="ps-text-composer"
          onSubmit={handleSubmit}
          className="mt-4 flex gap-2 shrink-0"
        >
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type your pitch or response..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            disabled={loading}
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="bg-green-700 text-white px-6 py-2 rounded-md font-medium hover:bg-green-800 transition-colors disabled:opacity-50"
          >
            Send
          </button>
        </form>
      )}
    </div>
  );
}
